import { useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Users, DollarSign } from "lucide-react";
import { useGymDebtors, MemberDebt } from "@/hooks/admin/useMemberDebts";
import { SettleDebtDialog } from "@/components/admin/debts/SettleDebtDialog";

interface DebtorsPanelProps {
  gymId: string;
}

export const DebtorsPanel = ({ gymId }: DebtorsPanelProps) => {
  const { data: debtors, isLoading } = useGymDebtors(gymId);
  const [selected, setSelected] = useState<{ debt: MemberDebt; name: string } | null>(null);

  const total = debtors?.length ?? 0;
  const totalOwed = (debtors ?? []).reduce((sum, d) => sum + Number(d.owed_amount), 0);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Deudores
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Users className="h-4 w-4" /> Total deudores
              </div>
              <div className="mt-1 text-2xl font-bold">{total}</div>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <DollarSign className="h-4 w-4" /> Total adeudado
              </div>
              <div className="mt-1 text-2xl font-bold">${totalOwed.toLocaleString()}</div>
            </div>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-6">Cargando...</p>
          ) : total === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No hay socios con deudas activas. 🎉
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {debtors!.map((d) => {
                const overdue = d.visualStatus === "overdue";
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() =>
                      setSelected({ debt: d as MemberDebt, name: d.displayName })
                    }
                    className="w-full text-left p-3 hover:bg-muted/50 transition-colors flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{d.displayName}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {d.category_name} · vence {format(new Date(d.due_date + "T00:00:00"), "dd MMM yyyy", { locale: es })}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold">${Number(d.owed_amount).toLocaleString()}</div>
                      <Badge
                        variant="outline"
                        className={
                          overdue
                            ? "border-red-500/60 text-red-600 dark:text-red-400 bg-red-500/10 text-[10px]"
                            : "border-amber-500/60 text-amber-600 dark:text-amber-400 bg-amber-500/10 text-[10px]"
                        }
                      >
                        {overdue ? "VENCIDO" : "En deuda"}
                      </Badge>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {selected && (
        <SettleDebtDialog
          open={!!selected}
          onOpenChange={(v) => !v && setSelected(null)}
          debt={selected.debt}
          userName={selected.name}
        />
      )}
    </>
  );
};
